import React, { FC, useContext } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons';
import { Box, Button, Flex, IconButton, Text, useTheme } from '@chakra-ui/react';
import { getTodayInTimezone } from '../../../../../studio_react/invoice/dateUtils';
import { DatePickerContext, ICustomDatePickerTheme } from './DatePicker';

export interface IDatePickerHeaderControlsProps {
    date: Date;
    decreaseMonth: () => void;
    increaseMonth: () => void;
    changeMonth: (month: number) => void;
    changeYear: (year: number) => void;
}

const DatePickerHeaderControls: FC<IDatePickerHeaderControlsProps> = ({
    date,
    decreaseMonth,
    increaseMonth,
    changeMonth,
    changeYear
}) => {
    const { centeredDate, showTodayButton, brandTimezone } = useContext(DatePickerContext);

    const theme = useTheme<ICustomDatePickerTheme>();
    const {
        color,
        headerBorderColor,
        headerPaddingBottom = '16px',
        chevronSize = '24px',
        chevronColor,
        monthYearFontSize = 'inherit',
        monthYearFontWeight = 'normal',
        todayButtonColor
    } = theme.components.DatePicker.baseStyle;

    const borderColor = headerBorderColor || color;
    const iconColor = chevronColor || color;

    const handleTodayClick = () => {
        // Use brand timezone if available, otherwise fall back to browser local time
        if (brandTimezone) {
            const todayString = getTodayInTimezone(brandTimezone); // Returns YYYY-MM-DD
            const [year, month] = todayString.split('-').map(Number);
            changeMonth(month - 1); // Months are 0-indexed
            changeYear(year);
        } else {
            const today = new Date();
            changeMonth(today.getMonth());
            changeYear(today.getFullYear());
        }
    };

    return (
        <Box
            paddingBottom={headerPaddingBottom}
            borderBottom={`1px solid ${borderColor}`}
            color={color}
        >
            {centeredDate ? (
                <Flex alignItems="center" justifyContent="center" width="100%">
                    <IconButton
                        aria-label="Previous month"
                        icon={<ChevronLeftIcon width={chevronSize} height={chevronSize} />}
                        onClick={decreaseMonth}
                        variant="ghost"
                        size="sm"
                        color={iconColor}
                        minWidth="auto"
                        height="auto"
                    />
                    <Text
                        paddingRight="30px"
                        paddingLeft="30px"
                        margin={0}
                        fontSize={monthYearFontSize}
                        fontWeight={monthYearFontWeight}
                        userSelect="none"
                    >
                        {date.toLocaleDateString('en-US', { month: 'long' })} {date.getFullYear()}
                    </Text>
                    <IconButton
                        aria-label="Next month"
                        icon={<ChevronRightIcon width={chevronSize} height={chevronSize} />}
                        onClick={increaseMonth}
                        variant="ghost"
                        size="sm"
                        color={iconColor}
                        minWidth="auto"
                        height="auto"
                    />
                </Flex>
            ) : (
                <Flex alignItems="center" justifyContent="space-between" width="100%">
                    <Flex alignItems="center" gap="12px">
                        <Text
                            margin={0}
                            fontSize={monthYearFontSize}
                            fontWeight={monthYearFontWeight}
                        >
                            {date.toLocaleDateString('en-US', { month: 'long' })}{' '}
                            {date.getFullYear()}
                        </Text>
                        {showTodayButton && (
                            <Button
                                size="xs"
                                variant="ghost"
                                onClick={handleTodayClick}
                                fontSize="12px"
                                fontWeight="400"
                                color={todayButtonColor || color}
                                _hover={{ backgroundColor: 'gray.100' }}
                            >
                                Today
                            </Button>
                        )}
                    </Flex>
                    <Flex alignItems="end" gap="8px">
                        <IconButton
                            aria-label="Previous month"
                            icon={<ChevronLeftIcon width={chevronSize} height={chevronSize} />}
                            onClick={decreaseMonth}
                            variant="ghost"
                            size="sm"
                            color={iconColor}
                            minWidth="auto"
                            height="auto"
                        />
                        <IconButton
                            aria-label="Next month"
                            icon={<ChevronRightIcon width={chevronSize} height={chevronSize} />}
                            onClick={increaseMonth}
                            variant="ghost"
                            size="sm"
                            color={iconColor}
                            minWidth="auto"
                            height="auto"
                        />
                    </Flex>
                </Flex>
            )}
        </Box>
    );
};

export default DatePickerHeaderControls;
